/**
 * Tutorial page — users can view all walkthrough sections for their role
 * and replay the interactive tour.
 */
import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../i18n/LanguageContext';
import { useTour } from '../components/TourProvider';
import { usePageTitle } from '../contexts/PageTitleContext';
import { PlayCircle, BookOpen, CheckCircle } from 'lucide-react';
import { getTourSteps } from '../config/tourSteps';

// Section definitions per role for the tutorial page
interface TutorialSection {
  titleKey: string;
  descriptionKey: string;
  route: string;
}

const operatorSections: TutorialSection[] = [
  { titleKey: 'tutorial.sections.dailyQueue', descriptionKey: 'tutorial.sections.dailyQueueDesc', route: '/schedule?tab=daily' },
  { titleKey: 'tutorial.sections.weeklySchedule', descriptionKey: 'tutorial.sections.weeklyScheduleDesc', route: '/schedule?tab=weekly' },
  { titleKey: 'tutorial.sections.production', descriptionKey: 'tutorial.sections.productionDesc', route: '/operator/production' },
];

const supervisorSections: TutorialSection[] = [
  { titleKey: 'tutorial.sections.supervisorDashboard', descriptionKey: 'tutorial.sections.supervisorDashboardDesc', route: '/supervisor' },
  { titleKey: 'tutorial.sections.productionPlanning', descriptionKey: 'tutorial.sections.productionPlanningDesc', route: '/production-planning' },
  { titleKey: 'tutorial.sections.schedule', descriptionKey: 'tutorial.sections.scheduleDesc', route: '/schedule' },
  { titleKey: 'tutorial.sections.transfers', descriptionKey: 'tutorial.sections.transfersDesc', route: '/transfers' },
  { titleKey: 'tutorial.sections.inventory', descriptionKey: 'tutorial.sections.inventoryDesc', route: '/inventory' },
  { titleKey: 'tutorial.sections.reports', descriptionKey: 'tutorial.sections.reportsDesc', route: '/supervisor/reports' },
  { titleKey: 'tutorial.sections.productionRecords', descriptionKey: 'tutorial.sections.productionRecordsDesc', route: '/production-records' },
];

const managerSections: TutorialSection[] = [
  { titleKey: 'tutorial.sections.managerDashboard', descriptionKey: 'tutorial.sections.managerDashboardDesc', route: '/manager' },
  ...supervisorSections.slice(1), // Same as supervisor minus the supervisor dashboard
];

const adminSections: TutorialSection[] = [
  { titleKey: 'tutorial.sections.adminDashboard', descriptionKey: 'tutorial.sections.adminDashboardDesc', route: '/dashboard' },
  { titleKey: 'tutorial.sections.userManagement', descriptionKey: 'tutorial.sections.userManagementDesc', route: '/users' },
  { titleKey: 'tutorial.sections.inventory', descriptionKey: 'tutorial.sections.inventoryDesc', route: '/inventory' },
  { titleKey: 'tutorial.sections.stationManagement', descriptionKey: 'tutorial.sections.stationManagementDesc', route: '/stations' },
  { titleKey: 'tutorial.sections.schedule', descriptionKey: 'tutorial.sections.scheduleDesc', route: '/schedule' },
  { titleKey: 'tutorial.sections.settings', descriptionKey: 'tutorial.sections.settingsDesc', route: '/settings' },
];

function getSections(role: string): TutorialSection[] {
  switch (role) {
    case 'O': case 'operator': return operatorSections;
    case 'S': case 'supervisor': return supervisorSections;
    case 'M': case 'manager': return managerSections;
    case 'A': case 'admin': return adminSections;
    default: return adminSections;
  }
}

const Tutorial: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { startTour, needsTour } = useTour();
  usePageTitle(t('tutorial.pageTitle'));

  const role = user?.role || 'O';
  const sections = getSections(role);
  const steps = getTourSteps(role);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BookOpen className="h-7 w-7 text-primary-600" />
              {t('tutorial.pageTitle')}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('tutorial.pageSubtitle')}
            </p>
          </div>
          <button
            onClick={startTour}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlayCircle className="h-5 w-5" />
            {t('tutorial.replayTour')}
          </button>
        </div>

        {/* Training status */}
        {!needsTour && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm">
            <CheckCircle className="h-4 w-4" />
            {t('tutorial.completed')}
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {sections.map((section, idx) => (
          <div
            key={idx}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t(section.titleKey)}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t(section.descriptionKey)}
                </p>
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                {section.route}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Tour info */}
      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          {t('tutorial.tourInfo').replace('{{count}}', String(steps.length))}
        </p>
      </div>
    </div>
  );
};

export default Tutorial;
